import { Injectable, inject } from '@angular/core';
import { create, toJson } from '@bufbuild/protobuf';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ProjectJobServiceCreateResponseJson,
  ProjectJobServiceCreateResponseSchema,
  ProjectJobServiceListRequestSchema,
  ProjectPermitTaskServiceCreateResponseJson,
  ProjectPermitTaskServiceCreateResponseSchema,
  ProjectPermitTaskServiceListRequestSchema,
  ProjectServiceCreateResponseJson,
  ProjectServiceCreateResponseSchema,
  ProjectSurveyTaskServiceCreateResponseJson,
  ProjectSurveyTaskServiceCreateResponseSchema,
  ProjectSurveyTaskServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { of } from 'rxjs';
import { catchError, concatMap, switchMap } from 'rxjs/operators';
import {
  PermitTaskRepositoryService,
  ProjectJobRepositoryService,
  ProjectRepositoryService,
  SurveyTaskRepositoryService,
} from '../../repositories';
import * as ProjectDetailsActions from './project-details.actions';

@Injectable()
export class ProjectDetailsEffects extends AuthRunEffects {
  private readonly repository = inject(ProjectRepositoryService);
  private readonly jobs = inject(ProjectJobRepositoryService);
  private readonly surveys = inject(SurveyTaskRepositoryService);
  private readonly permits = inject(PermitTaskRepositoryService);
  private readonly errors = inject(GlobalErrorHandlerService);

  loadProjectDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectDetailsActions.loadProjectDetails),
      switchMap(({ uuid }) =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        this.repository.fetch(uuid).pipe(
          concatMap((data) =>
            of(
              ProjectDetailsActions.loadProjectDetailsSuccess({
                data: toJson(
                  ProjectServiceCreateResponseSchema,
                  data,
                  defaultApiSerializeJsonOptions,
                ) as ProjectServiceCreateResponseJson,
              }),
              ProjectDetailsActions.loadProjectDetailsJobs({ uuid }),
              ProjectDetailsActions.loadProjectDetailsPermits({ uuid }),
              ProjectDetailsActions.loadProjectDetailsSurveys({ uuid }),
            ),
          ),
          catchError((error) => {
            this.errors.handleError(error);
            return of(
              ProjectDetailsActions.loadProjectDetailsFailure({ error }),
            );
          }),
        ),
      ),
    );
  });

  loadProjectDetailsJobs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectDetailsActions.loadProjectDetailsJobs),
      switchMap(({ uuid }) =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        this.jobs
          .list(
            create(ProjectJobServiceListRequestSchema, {
              projectUuid: uuid,
            }),
          )
          .pipe(
            concatMap(({ items }) =>
              of(
                ProjectDetailsActions.loadProjectDetailsJobsSuccess({
                  data: (items ?? []).map(
                    (item) =>
                      toJson(
                        ProjectJobServiceCreateResponseSchema,
                        item,
                        defaultApiSerializeJsonOptions,
                      ) as ProjectJobServiceCreateResponseJson,
                  ),
                }),
              ),
            ),
            catchError((error) => {
              this.errors.handleError(error);
              return of(
                ProjectDetailsActions.loadProjectDetailsJobsFailure({ error }),
              );
            }),
          ),
      ),
    );
  });

  loadProjectDetailsPermits$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectDetailsActions.loadProjectDetailsPermits),
      switchMap(({ uuid }) =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        this.permits
          .list(
            create(ProjectPermitTaskServiceListRequestSchema, {
              projectUuid: uuid,
            }),
          )
          .pipe(
            concatMap((data) =>
              of(
                ProjectDetailsActions.loadProjectDetailsPermitsSuccess({
                  data: (data.items ?? []).map(
                    (item) =>
                      toJson(
                        ProjectPermitTaskServiceCreateResponseSchema,
                        item,
                        defaultApiSerializeJsonOptions,
                      ) as ProjectPermitTaskServiceCreateResponseJson,
                  ),
                }),
              ),
            ),
            catchError((error) => {
              this.errors.handleError(error);
              return of(
                ProjectDetailsActions.loadProjectDetailsPermitsFailure({
                  error,
                }),
              );
            }),
          ),
      ),
    );
  });

  loadProjectDetailsSurveys$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectDetailsActions.loadProjectDetailsSurveys),
      switchMap(({ uuid }) =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        this.surveys
          .list(
            create(ProjectSurveyTaskServiceListRequestSchema, {
              projectUuid: uuid,
            }),
          )
          .pipe(
            concatMap((data) =>
              of(
                ProjectDetailsActions.loadProjectDetailsSurveysSuccess({
                  data: (data.items ?? []).map(
                    (item) =>
                      toJson(
                        ProjectSurveyTaskServiceCreateResponseSchema,
                        item,
                        defaultApiSerializeJsonOptions,
                      ) as ProjectSurveyTaskServiceCreateResponseJson,
                  ),
                }),
              ),
            ),
            catchError((error) => {
              this.errors.handleError(error);
              return of(
                ProjectDetailsActions.loadProjectDetailsSurveysFailure({
                  error,
                }),
              );
            }),
          ),
      ),
    );
  });

  constructor() {
    super(inject(Actions));
  }
}
